import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import DashboardManagement from "./DashboardManagement";
import "@cloudscape-design/global-styles/index.css";
import DashboardTable from "./DashboardTable";

const apiUrl = process.env.REACT_APP_API_URL;

// Configuration for retry logic
const RETRY_DELAY = 2000; // 2 seconds between retries
const MAX_RETRIES = 3;    // Maximum number of retry attempts
const REQUEST_TIMEOUT = 15000; // 15 seconds timeout

export default function PerformanceConfig() {
  const [dashboards, setDashboards] = useState([]);
  const [regions, setRegions] = useState([]);
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serviceStatus, setServiceStatus] = useState("available"); // "available", "degraded", "unavailable"
  const accessToken = useSelector((state) => state.accessToken.value);

  // Convert object data to array format
  function convertToObjectsArray(data) {
    return Object.keys(data).map((key) => ({
      name: key,
      description: data[key],
    }));
  }

  // Create a reusable function to handle API requests with retry logic
  const makeApiRequest = useCallback(async (
    query,
    variables = {},
    retryCount = 0,
    errorMessage = "An error occurred with the request"
  ) => {
    console.log("Making API request with query:", query);
    try {
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
          timeout: REQUEST_TIMEOUT
        }
      );
      console.log("API Response:", response);
      console.log(response)
      if (response.data.errors && response.data.errors[0].errorType === "Unauthorized") {
        setError("Unauthorized access. Please check your credentials.");
        return null;
      }
     
      // Return the response if we have data
      return response.data;
    } catch (err) {
      console.error(`${errorMessage}:`, err);

      // Handle resource errors specifically
      if (err.code === "ERR_INSUFFICIENT_RESOURCES" ||
        (err.message && err.message.includes("ERR_INSUFFICIENT_RESOURCES"))) {
        // Update service status
        setServiceStatus(retryCount >= MAX_RETRIES - 1 ? "unavailable" : "degraded");

        // Retry logic for resource errors
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying request (${retryCount + 1}/${MAX_RETRIES}) in ${RETRY_DELAY}ms...`);
          return new Promise(resolve => {
            setTimeout(async () => {
              const result = await makeApiRequest(query, variables, retryCount + 1, errorMessage);
              resolve(result);
            }, RETRY_DELAY * (retryCount + 1)); // Exponential backoff
          });
        }
      }

      setError(`${errorMessage}. Please try again later.`);
      return null;
    }
  }, [accessToken]);

  // Fetch initial data (regions and AWS accounts)
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      // Set service status to available at the start of new requests
      setServiceStatus("available");

      // Fetch regions
      const regionsQuery = `
        query GetDetails($id: String!) {
          getDetails(id: $id) {
            region
          }
        }`;

      const regionsData = await makeApiRequest(
        regionsQuery,
        { id: "1" },
        0,
        "Error fetching regions"
      );

      if (regionsData && regionsData.data && regionsData.data.getDetails) {
        const configurationDetails = JSON.parse(
          JSON.stringify(regionsData)
        );
        setRegions(
          convertToObjectsArray(
            JSON.parse(configurationDetails.data.getDetails.region)
          )
        );
      }

      // Fetch AWS accounts
      const accountsQuery = `
        query GetAWSAccounts {
          getAWSAccounts {
            AccountName
            AccountNo
            id
            Status
          }
        }`;

      const accountsData = await makeApiRequest(
        accountsQuery,
        {},
        0,
        "Error fetching AWS accounts"
      );

      if (accountsData && accountsData.data && accountsData.data.getAWSAccounts) {
        setAwsAccounts(accountsData.data.getAWSAccounts);
      }

      // Fetch existing dashboards
      await fetchDashboards();
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError("Failed to load initial data. Please try refreshing the page.");
      setServiceStatus("degraded");
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest]);

  // Fetch existing dashboards with the updated API structure
 // Updated fetchDashboards function in PerformanceConfig.jsx
const fetchDashboards = useCallback(async () => {
  setLoading(true);
  try {
    const query = `
      query GetDashboards {
        getDashboardData {
          accountNo
          details {
            metrics {
              alarmDetails {
                alarmName
                comparisonOperator
                resourceName
                statistic
                threshold
              }
              metricName
            }
            namespace
            resourceName
          }
          id
          name
          region
        }
      }`;

    const data = await makeApiRequest(
      query,
      {},
      0,
      "Error fetching dashboards"
    );

    if (data && data.data && data.data.getDashboardData) {
      // Transform the API data to match our expected format
      const formattedDashboards = data.data.getDashboardData.map((dashboard) => ({
        id: dashboard.id,
        name: dashboard.name,
        accountNo: dashboard.accountNo,
        region: dashboard.region,
        namespaces: dashboard.details.map((detail) => {
          // Create alarm configs structure from the API data
          const alarmConfigs = {};
          
          detail.metrics.forEach((metric) => {
            if (metric.alarmDetails && metric.alarmDetails.length > 0) {
              // Group alarm details by resource name for this metric
              const alarmsByResource = {};
              metric.alarmDetails.forEach((alarm) => {
                if (!alarmsByResource[alarm.resourceName]) {
                  alarmsByResource[alarm.resourceName] = [];
                }
                alarmsByResource[alarm.resourceName].push(alarm);
              });

              // Create alarm config structure
              alarmConfigs[metric.metricName] = {
                enabled: true,
                alarms: Object.keys(alarmsByResource).map((resourceName) => {
                  const alarmData = alarmsByResource[resourceName][0]; // Take first alarm for config
                  return {
                    threshold: alarmData.threshold,
                    statistic: alarmData.statistic,
                    comparisonOperator: alarmData.comparisonOperator,
                    resourceNames: [resourceName] // Store as array for consistency
                  };
                })
              };
            }
          });

          return {
            name: detail.namespace,
            metrics: detail.metrics.map((metric) => metric.metricName), // Extract just the metric names as strings
            resourceNames: detail.resourceName || [],
            alarmConfigs: alarmConfigs, // Add the reconstructed alarm configs
            // Keep the original metric objects for display purposes in the table
            metricsWithAlarms: detail.metrics
          };
        }),
      }));

      setDashboards(formattedDashboards);
    }
  } catch (err) {
    console.error("Error fetching dashboards:", err);
    setError("Failed to load dashboards. Please try refreshing the page.");
  } finally {
    setLoading(false);
  }
}, [makeApiRequest]);

  // Fetch namespaces for a selected account and region
  const fetchNamespaces = useCallback(async (accountNo, region) => {
    if (!accountNo || !region) return [];
    setLoading(true);

    try {
      // Add request debouncing - if multiple calls come in quickly, this helps prevent overloading
      await new Promise(resolve => setTimeout(resolve, 100));

      const query = `
        query GetNamespaces($accountNo: String, $region: String) {
          getNameSpaces(accountNo: $accountNo, region: $region) 
        }`;

      const variables = {
        accountNo: accountNo,
        region: region,
      };

      const data = await makeApiRequest(
        query,
        variables,
        0,
        "Error fetching namespaces"
      );

      if (data && data.data && data.data.getNameSpaces) {
        return data.data.getNameSpaces || [];
      }

      return [];
    } catch (err) {
      console.error("Error fetching namespaces:", err);
      setError("Failed to load namespaces. Please try again.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest]);

  // Fetch metrics for a selected namespace
  const fetchMetrics = useCallback(async (accountNo, region, namespace) => {
    if (!accountNo || !region || !namespace) return [];
    setLoading(true);

    try {
      const query = `
        query GetMetrics($accountNo: String, $region: String, $namespace: String) {
          getMetrics(accountNo: $accountNo, region: $region, namespace: $namespace) 
        }`;

      const variables = {
        accountNo: accountNo,
        region: region,
        namespace: namespace,
      };

      const data = await makeApiRequest(
        query,
        variables,
        0,
        "Error fetching metrics"
      );

      if (data && data.data && data.data.getMetrics) {
        return data.data.getMetrics || [];
      }

      return [];
    } catch (err) {
      console.error("Error fetching metrics:", err);
      setError("Failed to load metrics. Please try again.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest]);

  // Fetch resource names for a selected namespace
  const fetchResourceNames = useCallback(async (accountNo, region, namespace) => {
    if (!accountNo || !region || !namespace) return [];
    setLoading(true);

    try {
      const query = `
        query GetResourceNames($accountNo: String, $region: String, $namespace: String) {
          getResourceNames(accountNo: $accountNo, region: $region, namespace: $namespace) 
        }`;

      const variables = {
        accountNo: accountNo,
        region: region,
        namespace: namespace,
      };

      const data = await makeApiRequest(
        query,
        variables,
        0,
        "Error fetching resource names"
      );

      if (data && data.data && data.data.getResourceNames) {
        return data.data.getResourceNames || [];
      }

      return [];
    } catch (err) {
      console.error("Error fetching resource names:", err);
      setError("Failed to load resource names. Please try again.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest]);

  // Save dashboard (create or update) - Updated to handle new structure
// Save dashboard (create or update) - Updated to handle new structure
const saveDashboard = useCallback(async (dashboardData, isEditingDashboard) => {
  try {
    // Client-side validation before sending to server
    if (!isEditingDashboard) {
      const existingDashboard = dashboards.find(dashboard => 
        dashboard.accountNo === dashboardData.accountNo && 
        dashboard.region === dashboardData.region
      );
      
      if (existingDashboard) {
        setError(`Dashboard already exists for this account and region combination. Existing dashboard: "${existingDashboard.name}"`);
        return false;
      }
    }
    const details = dashboardData.namespaces.map((namespace) => {
      return {
        namespace: namespace.name,
        metrics: namespace.metrics.map((metric) => {
          const alarmConfig = namespace.alarmConfigs && namespace.alarmConfigs[metric];
          const hasAlarms = alarmConfig && alarmConfig.alarms && Array.isArray(alarmConfig.alarms);
          
          return {
            metricName: metric,
            alarmDetails: hasAlarms ? 
              alarmConfig.alarms.flatMap((alarmdetail) => {
                return alarmdetail.resourceNames.map((resourceName) => ({
                  alarmName: `dev-us-cloudlens-${metric}-${resourceName}`,
                  comparisonOperator: alarmdetail.comparisonOperator,
                  resourceName: resourceName,
                  statistic: alarmdetail.statistic,
                  threshold: alarmdetail.threshold
                }));
              }) : 
              (console.log("    No alarmConfigs found for metric:", metric), []),
          };
        }),
        resourceName: namespace.resourceNames || [],
      };
    });
    if (isEditingDashboard) {
      const query = `
mutation updateDashboard($input: updateDashboardInput!)
{
  updateDashboard(input: $input)
}
`;

      const data = await makeApiRequest(
        query,
        {
          input: {
            id: isEditingDashboard,
            name: dashboardData.name,
            details,
            accountNo: dashboardData.accountNo,
            region: dashboardData.region,
          }
        },
        0,
        "Error updating dashboard"
      );

      if (!data || data.errors) {
        setError(`Failed to update dashboard: ${data?.errors?.[0]?.message || "Unknown error"}`);
        return false;
      }

      console.log("Dashboard updated successfully");
    } else {
      const query = `
mutation createDashboard($input: CreateDashboardInput!)
{
  createDashboard(input: $input)
}
`;
      const data = await makeApiRequest(
        query,
        {
          input: {
            name: dashboardData.name,
            accountNo: dashboardData.accountNo,
            details,
            region: dashboardData.region,
          }
        },
        0,
        "Error creating dashboard"
      );
      const variables = {
        input: {
          name: dashboardData.name,
          details,
          accountNo: dashboardData.accountNo,
          region: dashboardData.region,
        }
      };
      if (!data || data.errors) {
        setError(`Failed to create dashboard: ${data?.errors?.[0]?.message || "Unknown error"}`);
        return false;
      }
      if (!data.data || !data.data.createDashboard) {
        setError("Failed to create dashboard: No data returned");
        return false;
      }
      console.log("Dashboard created successfully");
    }

    // Refresh dashboards list
    await fetchDashboards();
    return true;
  } catch (err) {
    console.error("Error saving dashboard:", err);
    setError(
      `Failed to ${isEditingDashboard ? "update" : "create"} dashboard. Please try again.`
    );
    return false;
  }
}, [makeApiRequest, fetchDashboards]);

  // Delete dashboard
  const deleteDashboard = useCallback(async (dashboardId) => {
    try {
      const query = `
         mutation DeleteDashboard($id: String!) {
          deleteDashboard(id: $id)
        }`;

      const variables = {
        id: dashboardId,
      };

      const data = await makeApiRequest(
        query,
        variables,
        0,
        "Error deleting dashboard"
      );

      if (!data || data.errors) {
        setError(`Failed to delete dashboard: ${data?.errors?.[0]?.message || "Unknown error"}`);
        return false;
      }

      setDashboards(dashboards.filter((d) => d.id !== dashboardId));
      return true;
    } catch (err) {
      console.error("Error deleting dashboard:", err);
      setError("Failed to delete dashboard. Please try again.");
      return false;
    }
  }, [makeApiRequest, dashboards]);

  // Load initial data on component mount
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <div className="bg-gradient-to-b from-[#003366]/5 to-white min-h-screen p-6">
      {/* Show service status indicator if there are issues */}
      {serviceStatus !== "available" && (
        <div className={`mb-4 p-3 rounded ${serviceStatus === "degraded"
          ? "bg-yellow-100 border border-yellow-400 text-yellow-700"
          : "bg-red-100 border border-red-400 text-red-700"
          }`}>
          <p className="font-medium">
            {serviceStatus === "degraded"
              ? "Service is experiencing slowness. Some features may take longer to respond."
              : "Service is currently unavailable. Please try again later."}
          </p>
        </div>
      )}

      <DashboardManagement
        dashboards={dashboards}
        regions={regions}
        awsAccounts={awsAccounts}
        loading={loading}
        error={error}
        setError={setError}
        fetchNamespaces={fetchNamespaces}
        fetchMetrics={fetchMetrics}
        fetchResourceNames={fetchResourceNames}
        saveDashboard={saveDashboard}
        deleteDashboard={deleteDashboard}
        serviceStatus={serviceStatus}
      />

      {/* Suggestion to check AWS resource limits if issues persist */}
      {serviceStatus !== "available" && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-800">
          <p className="text-sm">
            <strong>Admin Notice:</strong> The application is experiencing resource constraints.
            Please check your AWS service limits and available resources if this issue persists.
          </p>
        </div>
      )}
    </div>
  );
}